'use client';
import { useLayoutEffect, useState } from 'react';
import './index.css';
import SSVLink from '../../ssv-link';
import { usePingOA } from '@ssv-website/hooks';

interface INavTabsDataItem {
  minWidth?: string;
  // 标题
  title: string;
  // 链接地址
  href?: string;
  // 外链？
  isBlank?: boolean;
  alias?: string;
  // 子菜单
  subMenus?: INavTabsDataItem[];
  // 是否内网 OA 专属
  onlyOA?: boolean;
  // 移动端隐藏
  mobileHide?: boolean;
  // 大标题
  groupLabel?: string;
  //组排序
  groupOrder?: number;
}

export type NavTabsData = Array<INavTabsDataItem>;

export interface NavTabsProps {
  activeKey?: string;
  data: NavTabsData;
  className?: string;
  cssText?: string;
  cssLine?: string;
}

const useMultipleLinkNode = (data: NavTabsData) => {
  const [activeIndex, setActiveIndex] = useState<number>(999);
  const [subAubRoute, setSubRoute] = useState<string>('');

  const syncSubActIndex = (data?: NavTabsData) => {
    if (data) {
      const router = window.location.pathname;

      data.forEach((v) => {
        if (v.href !== '/' && v.href) {
          if (router.includes(v.href)) {
            setSubRoute(v.href);
          }
        }
      });
    }
  };

  useLayoutEffect(() => {
    const router = window.location.pathname;
    if (router === '/') {
      setActiveIndex(0);
      return;
    }

    // 找出所有匹配的菜单项
    const matches = data
      .map((d, idx) => ({ ...d, index: idx }))
      .filter((d) => {
        if (!d.alias) return false;
        if (d.isBlank) return false;
        return router.includes(d.alias);
      });

    // 优先选择 alias 最长的匹配项
    if (matches.length > 0) {
      const bestMatch = matches.reduce((prev, curr) =>
        (curr.alias?.length || 0) > (prev.alias?.length || 0) ? curr : prev
      );
      syncSubActIndex(bestMatch.subMenus);
      setActiveIndex(bestMatch.index);
    } else {
      setActiveIndex(0);
    }
  }, [data]);

  return {
    activeIndex,
    subAubRoute,
    setSubRoute,
  };
};

export const NavTabs: React.FC<NavTabsProps> = (props) => {
  const { data = [], className, cssText, cssLine } = props;
  const { activeIndex, subAubRoute, setSubRoute } = useMultipleLinkNode(data);
  const isOA = usePingOA({});

  return (
    <div className={`relative h-full ${className}`}>
      <div className="flex h-full">
        {data.map((m, index) => {
          // 是否只 OA 可见
          if (m.onlyOA && !isOA) return null;

          return (
            <div
              key={index}
              className="relative overflow-hidden first:pl-0 last:pr-0 px-[20px]  cursor-pointer group  hover:overflow-visible h-full flex items-center"
            >
              <div className="h-full flex items-center relative">
                <SSVLink
                  url={m.href}
                  key={index}
                  isBlank={m.isBlank}
                  className={`text-[16px] leading-6 text-primary cursor-pointer hover:text-active text-nowrap ${cssText}`}
                >
                  {m.title}
                </SSVLink>
                {index === activeIndex && (
                  <div
                    className={`absolute left-0 bottom-0 bg-primary duration-300 w-full h-1 -z-20 ${cssLine}`}
                  />
                )}
              </div>
              {m.subMenus && (
                <MenuList
                  data={m.subMenus}
                  activeIndex={activeIndex}
                  subAubRoute={subAubRoute}
                  setSubRoute={setSubRoute}
                  index={index}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
interface IMenuList {
  data: INavTabsDataItem[];
  activeIndex: number;
  subAubRoute: string;
  index: number;
  isBlank?: boolean;
  setSubRoute: (i: string) => void;
}
const ITEM_COUNT = 4;
const MenuList = (props: IMenuList) => {
  const { data, activeIndex, subAubRoute, index, setSubRoute } = props;

  const list = getDoubleDimensional(data, ITEM_COUNT);
  return (
    <div className="absolute rounded-md left-0 -translate-x-7 h-0 pt-[6px] top-full transition-all duration-300 group-hover:h-[186px]">
      <div
        className="bg-white overflow-hidden rounded-md py-[30px] flex gap-x-[26px]"
        style={{ boxShadow: '0px 6px 10px 0px rgba(114, 141, 178, 0.3)' }}
      >
        {list.map((v: any, i: number) => (
          <SubList
            key={i}
            data={v}
            activeIndex={activeIndex}
            subAubRoute={subAubRoute}
            setSubRoute={setSubRoute}
            index={index}
            needBorder={i > 0}
            baseCount={i * ITEM_COUNT}
          />
        ))}
      </div>
    </div>
  );
};

const SubList = (
  props: IMenuList & {
    needBorder?: boolean;
    baseCount: number;
    className?: string;
  }
) => {
  const {
    data,
    activeIndex,
    subAubRoute,
    index,
    needBorder,
    baseCount,
    className,
    setSubRoute,
  } = props;
  const getActCss = (i: string) => {
    if (i === subAubRoute) {
      return {
        color: '#0a65e0',
        borderLeft: '2px solid #0a65e0',
      };
    }
    return {};
  };

  const boxCss = `flex flex-col gap-y-[10px] ${
    needBorder ? 'border-l-[1px] border-[#353F49]/20' : ''
  }`;

  return (
    <div className={boxCss}>
      {data?.map((v: any, i) =>
        v.isGroup ? (
          <div
            key={i}
            className="pl-[30px] text-[16px] font-700 cursor-default"
          >
            {v.title}
          </div>
        ) : (
          <SSVLink
            key={i}
            url={v.href}
            isBlank={v.isBlank}
            onClick={() => {
              if (v.href?.includes('#')) {
                setSubRoute(v.href);
                return;
              }
              setSubRoute(v.href);
            }}
            className={`block pl-[30px] min-w-[130px] text-[14px] leading-[20px] text-primary hover:text-active relative bule-hover`}
            style={Object.assign({}, getActCss(v.href), {
              minWidth: v.minWidth ?? undefined,
            })}
          >
            {v.title}
          </SSVLink>
        )
      )}
    </div>
  );
};

const getDoubleDimensional = function <
  T extends { groupLabel?: any; groupOrder?: number }
>(list: T[], count: number) {
  const result: any = [];
  if (list?.[0]?.groupLabel) {
    let groupObj: Record<string, any> = {};
    list.forEach((v) => {
      if (v.groupLabel) {
        if (!groupObj[v.groupLabel]) {
          groupObj[v.groupLabel] = { data: [v], groupOrder: v.groupOrder };
        } else {
          groupObj[v.groupLabel].data.push(v);
        }
      }
    });
    Object.keys(groupObj)
      .sort((a, b) => groupObj[a].groupOrder - groupObj[b].groupOrder)
      .forEach((k) => {
        result.push([{ title: k, isGroup: true }, ...groupObj[k].data]);
      });
    return result;
  }
  for (let i = 0; i < list?.length; i += count) {
    result.push(list.slice(i, i + count));
  }

  return result;
};
